/**
 * Bootstrap reports providers. Combines everything needed to initialize the reports module.
 */
import { Observable } from 'rxjs';

import { createProviderToken, TypedProvider } from '@core/common-legacy';
import { HttpApi } from '@core/http-types';
import {
  ReportsAppKind,
  ReportDashboardMetricOptions,
  ReportsTranslations,
} from '@core/reports-types';

const { inject: injectReportsTranslations, provide: provideReportsTranslations } =
  createProviderToken<ReportsTranslations>('ReportsTranslations')();
const { inject: injectReportsMetrics, provide: provideReportsMetrics } =
  createProviderToken<Observable<ReportDashboardMetricOptions[]>>('ReportsMetrics')();
const { inject: injectReportsAppKind, provide: provideReportsAppKind } =
  createProviderToken<ReportsAppKind>('ReportsAppKind')();
const { inject: injectReportsCompanyId, provide: provideReportsCompanyId } = createProviderToken<
  string | number
>('ReportsCompanyId')();
// Api service used by all core reports data features
const { inject: injectReportsHttpApiService, provide: provideReportsHttpApiService } =
  createProviderToken<HttpApi>('ReportsApiService')();

export interface ReportsBootstrapProviders {
  appKind: ReportsAppKind;
  httpApiService: TypedProvider<HttpApi>;
  translations: TypedProvider<ReportsTranslations>;
  metrics: TypedProvider<Observable<ReportDashboardMetricOptions[]>>;
  companyId: TypedProvider<string | number>;
}

export const provideReports = ({
  appKind,
  httpApiService,
  metrics,
  translations,
  companyId,
}: ReportsBootstrapProviders) => [
  provideReportsAppKind({
    useValue: appKind,
  }),
  provideReportsHttpApiService(httpApiService),
  provideReportsMetrics(metrics),
  provideReportsTranslations(translations),
  ...(companyId ? [provideReportsCompanyId(companyId)] : []),
];

export {
  injectReportsAppKind,
  injectReportsHttpApiService,
  injectReportsTranslations,
  injectReportsMetrics,
  injectReportsCompanyId,
};
